import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col, Drawer, Row, Spin, Timeline,
} from 'antd';
import PropTypes from 'prop-types';
import { EditOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';

import { getIdMap } from '../../helpers/helpers';
import FormBuilder from '../../forms/FormBuilder/FormBuilder';
import { getSnapshotFormData } from '../../forms/formHelpers';
import DrawerSubmitFooter from '../containers/DrawerSubmitFooter';
import Step from '../containers/TimelineStep';
import OnTraccrEmpty from '../OnTraccrEmpty';
import OnTraccrCheckbox from '../inputs/OnTraccrCheckbox';

export default function SnapshotDrawer({
  id: itemId,
  open,
  onClose,
  snapshots = [],
  setSelectedFile,
  setSelectedFileDetails,
  isBoardCard,
  divisions,
  getSnapshotById,
}) {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users);
  const userMap = useMemo(() => getIdMap(users), [users]);

  const [selectedSnapshot, setSelectedSnapshot] = useState();
  const [selectedSnapshots, setSelectedSnapshots] = useState(new Set());
  const [loadingSnapshot, setLoadingSnapshot] = useState(null);
  const [
    isViewDifferenceToggled,
    setIsViewDifferenceToggled,
  ] = useState(false);

  const orderedSnapshots = useMemo(() => {
    const [
      firstSnapshotId,
      secondSnapshotId,
    ] = Array.from(selectedSnapshots);

    const firstSnapshot = snapshots.find(({ id }) => id === firstSnapshotId);
    const secondSnapshot = snapshots.find(({ id }) => id === secondSnapshotId);

    if (!firstSnapshot && !secondSnapshot) return null;
    if (!firstSnapshot) return [secondSnapshot];
    if (!secondSnapshot) return [firstSnapshot];
    return [firstSnapshot, secondSnapshot]
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [selectedSnapshots, isViewDifferenceToggled, snapshots]);

  const {
    formData,
    snapshotTitle,
  } = useMemo(() => {
    if (isViewDifferenceToggled && orderedSnapshots?.length === 2) {
      const [
        firstSnapshotState,
        secondSnapshotState,
      ] = orderedSnapshots;

      const firstSnapshot = snapshots.find(({ id }) => id === firstSnapshotState.id);
      const secondSnapshot = snapshots.find(({ id }) => id === secondSnapshotState.id);

      const firstSnapshotTitle = DateTime
        .fromMillis(firstSnapshot.timestamp)
        .toLocaleString(DateTime.DATETIME_MED);
      const secondSnapshotTitle = DateTime
        .fromMillis(secondSnapshot.timestamp)
        .toLocaleString(DateTime.DATETIME_MED);

      return {
        formData: getSnapshotFormData({
          snapshot: firstSnapshot,
          secondarySnapshot: secondSnapshot,
          setSelectedFile,
          setSelectedFileDetails,
        }),
        snapshotTitle: `${firstSnapshotTitle} - ${secondSnapshotTitle} Difference`,
      };
    }

    if (selectedSnapshot || orderedSnapshots?.length) {
      const relevantSnapshot = orderedSnapshots?.[0] || selectedSnapshot;
      return {
        formData: getSnapshotFormData({
          snapshot: relevantSnapshot,
          setSelectedFile,
          setSelectedFileDetails,
        }),
        snapshotTitle: DateTime
          .fromMillis(relevantSnapshot.timestamp)
          .toLocaleString(DateTime.DATETIME_MED),
      };
    }

    return {
      formData: null,
    };
  }, [
    snapshots,
    selectedSnapshot,
    orderedSnapshots,
    isViewDifferenceToggled,
    setSelectedFile,
    setSelectedFileDetails,
  ]);

  const onSnapshotClick = useCallback(async (snapshot) => {
    if (loadingSnapshot || snapshot.id === selectedSnapshot?.id) return;
    if (snapshot.dataLoaded) {
      setSelectedSnapshot(snapshot);
      return;
    }
    setLoadingSnapshot(snapshot.id);
    const result = await dispatch(getSnapshotById(itemId, snapshot.id));
    if (result) {
      setSelectedSnapshot(result);
    }
    setLoadingSnapshot(null);
  }, [getSnapshotById, itemId, loadingSnapshot, selectedSnapshot]);

  const onViewDifferenceClick = useCallback(async () => {
    if (orderedSnapshots?.length !== 2) return;
    const [
      firstSnapshot,
      secondSnapshot,
    ] = orderedSnapshots;
    setLoadingSnapshot(firstSnapshot?.id);
    if (!firstSnapshot?.dataLoaded) {
      await dispatch(getSnapshotById(itemId, firstSnapshot.id));
    }
    setLoadingSnapshot(secondSnapshot?.id);
    if (!secondSnapshot?.dataLoaded) {
      await dispatch(getSnapshotById(itemId, secondSnapshot.id));
    }
    setLoadingSnapshot(null);
    setIsViewDifferenceToggled(true);
  }, [orderedSnapshots, getSnapshotById, itemId]);

  const onCheckChange = useCallback((snapshot) => async (checked) => {
    if (checked || (selectedSnapshots.size < 2 && !selectedSnapshots.has(snapshot.id))) {
      if (!snapshot.dataLoaded) {
        setLoadingSnapshot(snapshot.id);
        await dispatch(getSnapshotById(itemId, snapshot.id));
        setLoadingSnapshot(null);
      }
      setSelectedSnapshots((prev) => new Set([...prev, snapshot.id]));
      return;
    }
    setSelectedSnapshots((prev) => {
      const newSet = new Set(prev);
      newSet.delete(snapshot.id);
      return newSet;
    });
  }, [selectedSnapshots, isViewDifferenceToggled, getSnapshotById, itemId]);

  const onMainDrawerClose = () => {
    onClose();
    setSelectedSnapshot();
    setSelectedSnapshots(new Set());
    setIsViewDifferenceToggled();
  };

  const snapshotDivisions = useMemo(() => {
    if (divisions) return divisions;

    const relevantSnapShot = selectedSnapshot ?? orderedSnapshots?.[0];
    const {
      templateSchema: {
        divisionId,
      } = {},
    } = relevantSnapShot || {};
    return divisionId ? [divisionId] : [];
  }, [selectedSnapshot, orderedSnapshots, divisions]);

  const formId = useMemo(() => {
    const relevantSnapshot = selectedSnapshot ?? orderedSnapshots?.[0];
    return relevantSnapshot?.formId;
  }, [selectedSnapshot, orderedSnapshots]);

  return (
    <>
      <Drawer
        title="Snapshots"
        placement="right"
        closable
        onClose={onMainDrawerClose}
        visible={open}
        width="calc(50% - 25px)"
      >
        <Timeline>
          {snapshots.map((snapshot) => (
            <Step
              key={snapshot.id}
              Icon={EditOutlined}
              title={`${isBoardCard ? 'Card' : 'Form'} Updated`}
              text={`${userMap[snapshot.updaterId]?.name ?? 'Unknown'} updated the ${isBoardCard ? 'card' : 'form'}`}
              timestamp={snapshot.timestamp}
              action={{
                title: 'View',
                onClick: () => onSnapshotClick(snapshot),
                disabled: !!selectedSnapshots.size,
              }}
              loading={loadingSnapshot === snapshot.id}
              disabled={loadingSnapshot && loadingSnapshot !== snapshot.id}
              IconNode={(
                <OnTraccrCheckbox
                  disabled={selectedSnapshots.size === 2 && !selectedSnapshots.has(snapshot.id)}
                  value={selectedSnapshots.has(snapshot.id)}
                  onChange={onCheckChange(snapshot)}
                />
              )}
              onSelect={onCheckChange(snapshot)}
              isSelected={selectedSnapshots.has(snapshot.id)}
            />
          ))}
        </Timeline>
        <DrawerSubmitFooter
          onClose={onMainDrawerClose}
          submitTitle="View Difference"
          canSubmit={selectedSnapshots.size === 2}
          onSubmit={isViewDifferenceToggled
            ? null
            : onViewDifferenceClick}
        />
      </Drawer>
      <Drawer
        title={snapshotTitle}
        placement="left"
        closable
        onClose={() => {
          setSelectedSnapshot(null);
          setIsViewDifferenceToggled();
        }}
        visible={!!selectedSnapshot || isViewDifferenceToggled}
        width="calc(50% - 25px)"
        bodyStyle={{ padding: 0 }}
        mask={false}
      >
        <div style={{ position: 'relative', minHeight: '100%' }}>
          { !!formData && (
            <FormBuilder
              formId={formId}
              divisions={snapshotDivisions}
              isDisplay
              initialSections={formData}
              shouldHideCustomRenderingFields
              style={{ top: 75 }}
              isDetailView
            />
          )}
          { !formData && (
            <OnTraccrEmpty title="Form Fields" />
          )}
          {loadingSnapshot && (
            <Row
              style={{ position: 'absolute', inset: 0 }}
              justify="center"
              align="middle"
            >
              <Col>
                <Spin />
              </Col>
            </Row>
          )}
        </div>
      </Drawer>
    </>
  );
}

SnapshotDrawer.propTypes = {
  id: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  snapshots: PropTypes.arrayOf({
    id: PropTypes.string,
    name: PropTypes.string,
    createdAt: PropTypes.string,
  }),
  setSelectedFile: PropTypes.func.isRequired,
  setSelectedFileDetails: PropTypes.func.isRequired,
  isBoardCard: PropTypes.bool,
  divisions: PropTypes.arrayOf(PropTypes.string),
  getSnapshotById: PropTypes.func.isRequired,
};

SnapshotDrawer.defaultProps = {
  id: null,
  open: false,
  snapshots: [],
  isBoardCard: false,
  divisions: null,
};
